// src/utils/diagnostics/fetchCompletedDiagnostic.js
import {
  doc,
  getDoc,
  getDocs,
  query,
  where,
  documentId,
  collection,
} from "firebase/firestore";
import { db } from "../../firebase/firebase";

/* Firestore "in" query max is 10 */
function chunkArray(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/**
 * Loads a completed diagnostic attempt + its definition + its questions.
 * Expects:
 * - attempt at users/{studentId}/diagnosticAttempts/{attemptId}
 * - definition at diagnostics/{diagnosticId}
 * - questions in questions/{questionId}
 */
export async function fetchCompletedDiagnostic({ studentId, attemptId }) {
  if (!studentId) throw new Error("fetchCompletedDiagnostic: missing studentId");
  if (!attemptId) throw new Error("fetchCompletedDiagnostic: missing attemptId");

  // ---- attempt
  const attemptRef = doc(db, "users", studentId, "diagnosticAttempts", attemptId);
  const attemptSnap = await getDoc(attemptRef);

  if (!attemptSnap.exists()) {
    return { attempt: null, definition: null, questions: [] };
  }

  const attempt = { id: attemptSnap.id, ...attemptSnap.data() };

  const diagnosticId = attempt.diagnosticId || attempt.diagnosticDefinitionId || attempt.id;
  if (!diagnosticId) {
    return { attempt, definition: null, questions: [] };
  }

  // ---- definition
  const defRef = doc(db, "diagnostics", diagnosticId);
  const defSnap = await getDoc(defRef);
  const definition = defSnap.exists() ? { id: defSnap.id, ...defSnap.data() } : null;

  // ---- questions
  const sequence = Array.isArray(attempt.sequence) ? attempt.sequence : [];
  const uniqueIds = Array.from(new Set(sequence)).filter(Boolean);

  if (uniqueIds.length === 0) {
    return { attempt, definition, questions: [] };
  }

  const questionsCol = collection(db, "questions");

  const chunks = chunkArray(uniqueIds, 10);
  const fetched = [];

  for (const ids of chunks) {
    const q = query(questionsCol, where(documentId(), "in", ids));
    const snap = await getDocs(q);

    snap.forEach((d) => {
      // ✅ IMPORTANT: include Firestore doc id so ReviewDiagnosticPage can match sequence IDs
      fetched.push({ id: d.id, ...d.data() });
    });
  }

  return { attempt, definition, questions: fetched };
}
